import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import {
  ShieldCheck,
  Lock,
  Database,
  UserCheck,
  Globe
} from 'lucide-react';

const PrivacyPolicy = () => {
  const privacySections = [
    {
      title: 'Information Collection',
      description: 'We collect data to provide and improve our services',
      icon: Database
    },
    {
      title: 'Data Protection',
      description: 'Your personal information is encrypted and secured',
      icon: Lock
    },
    {
      title: 'User Consent',
      description: 'We obtain explicit consent for data processing',
      icon: UserCheck
    },
    {
      title: 'Global Compliance',
      description: 'Adhering to international privacy regulations',
      icon: Globe
    }
  ];

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Privacy <span className="text-primary">Policy</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Your trust is our priority. We are committed to protecting your personal information.
          </p>
        </section>

        {/* Privacy Highlights */}
        <section className="mb-16">
          <div className="grid md:grid-cols-4 gap-8">
            {privacySections.map((section, index) => (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <section.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{section.title}</h3>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Detailed Privacy Policy */}
        <section className="max-w-4xl mx-auto">
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle>Detailed Privacy Policy</CardTitle>
              <CardDescription>
                Comprehensive overview of our data handling practices
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">1. Information We Collect</h3>
                  <p className="text-muted-foreground">
                    We collect personal information that you voluntarily provide to us,
                    such as name, email address, and company details when you use our services.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">2. How We Use Your Information</h3>
                  <p className="text-muted-foreground">
                    We use the information we collect to provide, maintain, and improve our services,
                    communicate with you, and ensure the security of our platform.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">3. Data Protection</h3>
                  <p className="text-muted-foreground">
                    We implement industry-standard security measures to protect your personal information
                    from unauthorized access, alteration, disclosure, or destruction.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">4. Your Rights</h3>
                  <p className="text-muted-foreground">
                    You have the right to access, correct, or delete your personal information.
                    You can also opt-out of certain data processing activities.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Compliance Statement */}
        <section className="text-center mt-16 max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Our Commitment to Privacy</h2>
          <p className="text-muted-foreground mb-8">
            We are committed to maintaining the accuracy, confidentiality, and security of your personal information.
            Our privacy practices are designed to comply with global data protection regulations.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
